import React, { useEffect, useState } from "react";
import { api, rupiah, formatDate, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { Check, Crown, Loader2 } from "lucide-react";

export default function Subscription() {
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(null);

  const load = () => {
    api.get("/subscription/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const upgrade = async (plan) => {
    setLoading(plan);
    try {
      await api.post("/subscription/upgrade", { plan });
      toast.success("Paket berhasil diaktifkan!");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(null); }
  };

  if (!sub) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Paket Berlangganan</h1>
        <p className="text-muted-foreground text-sm">Kelola paket langganan toko Anda.</p>
      </div>

      <Card className="p-6 border-slate-200 rounded-xl shadow-sm bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-slate-300 text-sm">Paket Aktif Saat Ini</div>
            <div className="font-heading text-3xl font-extrabold mt-1 flex items-center gap-2">
              <Crown className="w-7 h-7 text-primary" /> {sub.plan_name}
            </div>
            <div className="mt-2 text-sm text-slate-300">
              {sub.subscription_end ? <>Berlaku hingga {formatDate(sub.subscription_end)} • {sub.days_left >= 0 ? `${sub.days_left} hari lagi` : "sudah lewat"}</> : "Tanpa batas waktu"}
            </div>
          </div>
          <StatusBadge status={sub.status} testid="current-sub-status" />
        </div>
        {sub.status === "due" && <div className="mt-4 text-sm bg-amber-500/20 text-amber-200 rounded-lg px-3 py-2">Langganan Anda akan segera jatuh tempo. Perpanjang untuk menghindari gangguan layanan.</div>}
        {sub.status === "expired" && <div className="mt-4 text-sm bg-red-500/20 text-red-200 rounded-lg px-3 py-2">Langganan Anda telah expired. Beberapa fitur mungkin dibatasi.</div>}
      </Card>

      <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
        <b>Catatan:</b> Pembayaran otomatis (Stripe) belum tersedia untuk Indonesia. Aktivasi paket saat ini bersifat <b>simulasi</b> — integrasi gateway lokal (Midtrans) dapat ditambahkan nanti.
      </div>

      <div className="grid md:grid-cols-3 gap-5 items-start">
        {plans.map((p) => {
          const current = sub.plan === p.id;
          return (
            <Card key={p.id} className={`p-6 border rounded-2xl ${p.id === "pro" ? "border-primary ring-2 ring-primary/20 shadow-lg" : "border-slate-200 shadow-sm"}`} data-testid={`plan-card-${p.id}`}>
              {p.id === "pro" && <div className="text-xs font-bold uppercase tracking-wider text-primary mb-2">Populer</div>}
              <h3 className="font-heading font-bold text-xl">{p.name}</h3>
              <div className="mt-2 flex items-end gap-1">
                <span className="font-heading text-3xl font-extrabold">{p.price === 0 ? "Gratis" : rupiah(p.price)}</span>
                {p.price > 0 && <span className="text-muted-foreground text-sm mb-1">/{p.period}</span>}
              </div>
              <ul className="mt-5 space-y-2.5">
                {p.features.map((f, i) => <li key={i} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-emerald-500 shrink-0" /> {f}</li>)}
              </ul>
              <Button
                onClick={() => upgrade(p.id)}
                disabled={current || loading === p.id}
                variant={p.id === "pro" ? "default" : "outline"}
                className="w-full rounded-lg mt-6"
                data-testid={`upgrade-${p.id}-btn`}
              >
                {loading === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : current ? "Paket Aktif" : "Pilih Paket"}
              </Button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
